// ============================================================
// DEALIN MVP - Auth API
// ============================================================

import apiClient, { setTokens, clearTokens } from './client';
import type { LoginCredentials, LoginResponse, UserContext } from '@/types';

// ------------------------------------------------------------
// Get CSRF Token (call before POST requests)
// ------------------------------------------------------------

export const ensureCSRFToken = async (): Promise<void> => {
  // Make a GET request to get CSRF cookie from Django
  try {
    await apiClient.get('/csrf/');
  } catch {
    // Try alternate endpoint
    try {
      await apiClient.get('/user/context/');
    } catch {
      // Ignore - just trying to get the cookie
    }
  }
};

// ------------------------------------------------------------
// Login
// ------------------------------------------------------------

export const login = async (credentials: LoginCredentials): Promise<LoginResponse> => {
  // Ensure we have CSRF token before POST
  await ensureCSRFToken();

  const pendingSessionId = localStorage.getItem('pendingBriefSessionId');
  const pendingAIChatSessionId = localStorage.getItem('pendingAIChatSessionId');
  const payload: Record<string, unknown> = { ...credentials };
  if (pendingSessionId) {
    payload.brief_session_id = pendingSessionId;
  }
  if (pendingAIChatSessionId) {
    payload.ai_chat_session_id = pendingAIChatSessionId;
  }
  
  const response = await apiClient.post<LoginResponse>('/auth/login/', payload);
  
  if (response.data.success && response.data.tokens) {
    setTokens(response.data.tokens);
    localStorage.removeItem('pendingBriefSessionId');
    localStorage.removeItem('pendingAIChatSessionId');
  }
  
  return response.data;
};

// ------------------------------------------------------------
// Logout
// ------------------------------------------------------------

export const logout = async (): Promise<void> => {
  try {
    const refreshToken = localStorage.getItem('refresh_token');
    await apiClient.post('/auth/logout/', refreshToken ? { refresh: refreshToken } : {});
  } catch {
    // Ignore errors, we're logging out anyway
  } finally {
    clearTokens();
  }
};

// ------------------------------------------------------------
// Get User Context
// ------------------------------------------------------------

export const getUserContext = async (): Promise<UserContext> => {
  const response = await apiClient.get<UserContext>('/user/context/');
  return response.data;
};

// ------------------------------------------------------------
// Refresh Token (usually handled by interceptor, but exposed for manual use)
// ------------------------------------------------------------

export const refreshToken = async (refresh: string): Promise<{ access: string }> => {
  const response = await apiClient.post<{ access: string }>('/auth/refresh/', { refresh });
  return response.data;
};

// ------------------------------------------------------------
// Session Login (for Django session-based auth fallback)
// ------------------------------------------------------------

export const sessionLogin = async (credentials: LoginCredentials): Promise<LoginResponse> => {
  const response = await apiClient.post<LoginResponse>('/auth/session/', credentials);
  
  if (response.data.success && response.data.tokens) {
    setTokens(response.data.tokens);
  }
  
  return response.data;
};

// ------------------------------------------------------------
// Registration
// ------------------------------------------------------------

export interface RegisterData {
  username: string;
  email: string;
  password: string;
  password_confirm: string;
  user_type: 'consulente' | 'cliente';
  company_name: string;
  first_name?: string;
  last_name?: string;
  sector?: string;
  website?: string;
  role?: string;
  description?: string;
  phone?: string;
  vat_number?: string;
  // Indirizzo strutturato (Google Places)
  address_text?: string;
  address_line1?: string;
  city?: string;
  province?: string;
  postal_code?: string;
  country?: string;
  country_code?: string;
  place_id?: string;
  latitude?: number | null;
  longitude?: number | null;
  // Tassonomie
  languages?: string;
  industries?: string;
  areas?: string;
  tools?: string;
  certifications?: string;
}

export interface RegisterResponse {
  success: boolean;
  pending_approval?: boolean;
  message?: string;
  tokens?: {
    access: string;
    refresh: string;
  };
  user?: {
    user: {
      id: number;
      username: string;
      email: string;
    };
    profile: Record<string, unknown>;
    vendor: Record<string, unknown> | null;
    is_vendor: boolean;
    is_client: boolean;
    is_authenticated: boolean;
  };
  errors?: Record<string, string[]>;
  error?: string;
}

export const register = async (data: RegisterData): Promise<RegisterResponse> => {
  // Ensure we have CSRF token before POST
  await ensureCSRFToken();

  const pendingSessionId = localStorage.getItem('pendingBriefSessionId');
  const pendingAIChatSessionId = localStorage.getItem('pendingAIChatSessionId');
  const payload: Record<string, unknown> = { ...data };
  if (pendingSessionId) {
    payload.brief_session_id = pendingSessionId;
  }
  if (pendingAIChatSessionId) {
    payload.ai_chat_session_id = pendingAIChatSessionId;
  }
  
  const response = await apiClient.post<RegisterResponse>('/auth/register/', payload);
  
  if (response.data.success && response.data.tokens) {
    setTokens(response.data.tokens);
    localStorage.removeItem('pendingBriefSessionId');
    localStorage.removeItem('pendingAIChatSessionId');
  }
  
  return response.data;
};

// ------------------------------------------------------------
// Google Login / Registration
// ------------------------------------------------------------

export interface GoogleLoginData {
  credential?: string;
  access_token?: string;
}

export interface GoogleLoginResponse extends LoginResponse {
  created?: boolean;
  needs_phone?: boolean;
}

export const googleLogin = async (data: GoogleLoginData): Promise<GoogleLoginResponse> => {
  await ensureCSRFToken();

  const pendingSessionId = localStorage.getItem('pendingBriefSessionId');
  const pendingAIChatSessionId = localStorage.getItem('pendingAIChatSessionId');
  const payload: Record<string, unknown> = { ...data };
  if (pendingSessionId) {
    payload.brief_session_id = pendingSessionId;
  }
  if (pendingAIChatSessionId) {
    payload.ai_chat_session_id = pendingAIChatSessionId;
  }

  const response = await apiClient.post<GoogleLoginResponse>('/auth/google/', payload);

  if (response.data.success && response.data.tokens) {
    setTokens(response.data.tokens);
    localStorage.removeItem('pendingBriefSessionId');
    localStorage.removeItem('pendingAIChatSessionId');
  }

  return response.data;
};

// ------------------------------------------------------------
// Profile
// ------------------------------------------------------------

export const getProfile = async () => {
  const response = await apiClient.get('/profile/');
  return response.data;
};

export const updateProfile = async (data: Record<string, unknown>) => {
  const response = await apiClient.put('/profile/', data);
  return response.data;
};

// ------------------------------------------------------------
// Vendor Profile
// ------------------------------------------------------------

export const getVendorProfile = async () => {
  const response = await apiClient.get('/vendor/profile/');
  return response.data;
};

export const updateVendorProfile = async (data: Record<string, unknown> | FormData) => {
  const isFormData = data instanceof FormData;
  const response = await apiClient.put('/vendor/profile/', data, isFormData ? {
    headers: { 'Content-Type': 'multipart/form-data' },
  } : undefined);
  return response.data;
};

// ------------------------------------------------------------
// Onboarding
// ------------------------------------------------------------

export const onboardingStep2 = async (data: Record<string, unknown> | FormData) => {
  const isFormData = data instanceof FormData;
  const response = await apiClient.post('/onboarding/step2/', data, isFormData ? {
    headers: { 'Content-Type': 'multipart/form-data' },
  } : undefined);
  return response.data;
};

export const onboardingStep3 = async (data: Record<string, unknown>) => {
  const response = await apiClient.post('/onboarding/step3/', data);
  return response.data;
};

export const onboardingComplete = async () => {
  const response = await apiClient.post('/onboarding/complete/');
  return response.data;
};
